import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import createMarkup from 'create-markup';
import { graphql } from 'gatsby';
import { localizePath } from "../../helpers/localeHelpers";

// hoc
import withI18next from '../../i18n/withI18next';

// hooks
import useSmallScreen from '../../hooks/useSmallScreen';
import useImageColorDetection from '../../hooks/useImageColorDetection';
import { useTranslation } from 'react-i18next';

// components
import Layout from '../../components/layout';
import ArticleContentWrapper from '../../components/ArticleContentWrapper/ArticleContentWrapper';
import ArticleContent from '../../components/ArticleContent/ArticleContent';
import BlueDivide from '../../components/BlueDivide/BlueDivide';
import NewsItem from '../../components/NewsItem/NewsItem';
import BackgroundWithLinesHalf from '../../components/Background/BackgroundWithLinesHalf';
import SectionWhiteFirst from '../../components/PageSections/SectionWhiteFirst';
import SectionWhiteSecond from '../../components/PageSections/SectionWhiteSecond';
import SectionBlueFirst from '../../components/PageSections/SectionBlueFirst';
import SectionBlueHalfPage from '../../components/PageSections/SectionBlueHalfPage';
import SectionWhiteHalfPage from '../../components/PageSections/SectionWhiteHalfPage';
import { Image } from '../../components/Image/Image';
import { Script } from '../../components/Script/Script';


// styles
import './news-page.scss';
import {languageSelectorLocalization} from '../../helpers/localeHelpers';

const NewsPage = ({data, location}) => {
  const {smallScreen} = useSmallScreen();
  const layoutRef = useRef();
  const [logoColor, setLogoColor] = useState();
  const {invertedColor, imageWrapperRef, detectImageColor} = useImageColorDetection();
  const { t } = useTranslation();

  useEffect(() => {
      if(invertedColor === 'blue') {
        setLogoColor(invertedColor);
        updateLogoColor(smallScreen ? invertedColor : 'white');
        return;
      }
  
      updateLogoColor('white');

    return () => {};
  }, [invertedColor, smallScreen]);
  
  const updateLogoColor = color => {
    layoutRef.current.handleLogoColor(color);
  };

  const { contentfulNewsPage, news, localizations } = data;
  if(!contentfulNewsPage) return null;

  const {
    body,
    type,
    image,
    date,
    quote,
    relatedNews,
    relatedNewsHeading,
    metaTitle,
    metaDescription,
    keywords,
    ogImage,
    script
  } = contentfulNewsPage;

  const languageSelectorInformation = languageSelectorLocalization('/nyheter', localizations);

  const relatedNewsSection = relatedNews
    ? relatedNews.map(item => {
        const { title, date, type, slug, image, language } = item;
        return (
          <NewsItem
            key={slug}
            slug={localizePath(slug, language)}
            image={image.fluid}
            title={title}
            type={type}
            date={date}
          />
        );
      })
    : news.edges.map(item => {
        const { title, date, type, slug, image, language } = item.node;
        return (
          <NewsItem
            key={slug}
            slug={localizePath(slug, language)}
            image={image.fluid}
            title={title}
            type={type}
            date={date}
          />
        );
      });

  return (
    <Layout
      title={metaTitle}
      description={metaDescription}
      keywords={keywords}
      ogImage={ogImage && ogImage.file.url}
      location={location}
      ref={layoutRef}
      halfPage
      logoColor={logoColor}
      languageSelectorInformation={languageSelectorInformation}>
      <div className="news-page">
        <div className="news-page__container">
          <div className="--left" ref={imageWrapperRef}>
            <Image fluid={image.fluid} className="news-page__image" onLoad={() => detectImageColor()} />
          </div>
          <div className="--right">
            <BackgroundWithLinesHalf theme={'white'}>
              {smallScreen && (
                <SectionWhiteFirst initialLogoColor={logoColor} logoColor={updateLogoColor} />
              )}
              {!smallScreen && (
                <SectionWhiteHalfPage logoColor={updateLogoColor} />
              )}

              <ArticleContentWrapper>
                <ArticleContent>
                  <div className="news-page__date">
                    {t(`filters.${type}`)} / {date}
                  </div>
                  <div
                    className="news-page__body-text"
                    dangerouslySetInnerHTML={createMarkup(
                      body.childMarkdownRemark.html
                    )}
                  />
                  {quote && (
                    <blockquote
                      className="news-page__quote-text"
                      dangerouslySetInnerHTML={createMarkup(
                        quote.quoteText.quoteText
                      )}
                    />
                  )}
                  {quote && (
                    <span className="news-page__quote-author">
                      {quote.author}
                      {quote.titleAndCompany &&
                        ", " + quote.titleAndCompany
                  }
                    </span>
                  )}
                </ArticleContent>
              </ArticleContentWrapper>
            </BackgroundWithLinesHalf>
            {smallScreen && (
              <SectionBlueFirst logoColor={updateLogoColor} />
            )}
            {!smallScreen && (
              <SectionBlueHalfPage logoColor={updateLogoColor} />
            )}
            {!!relatedNewsSection.length &&
              <><BlueDivide>
                <h2 className="news-page__related-news-heading">
                  {relatedNewsHeading.headingText}
                </h2>
              </BlueDivide>
              <BackgroundWithLinesHalf theme={'white'}>
                {smallScreen && (
                  <SectionWhiteSecond logoColor={updateLogoColor} />
                )}
                <div className="news-page__related-news-box">
                  <div className="related-area-container">
                    <div className="related-area">{relatedNewsSection}</div>
                  </div>
                </div>
              </BackgroundWithLinesHalf></>
            }
          </div>
        </div>
      </div>
      {script &&
        <Script script={script.script}/>
      }
    </Layout>
  );
}

export default withI18next()(NewsPage);

NewsPage.defaultProps = {
  data: {
    contentfulNewsPage: {
      title: '',
      cases: []
    }
  },
  quote: {
    quoteText: {
      quoteText: ''
    },
    author: ''
  },
  logoColor: () => {}
};

NewsPage.propTypes = {
  data: PropTypes.object,
  quote: PropTypes.object,
  logoColor: PropTypes.func
};

export const pageQuery = graphql`
  query newsPageQuery($trueSlug: String!, $locale: String!) {
    contentfulNewsPage(slug: { eq: $trueSlug }, language: {eq: $locale}) {
      title
      metaTitle
      metaDescription
      keywords
      ogImage{
        file{
          url
        }
      }
      type
      script{
        script
      }
      body {
        id
        childMarkdownRemark {
          id
          html
        }
      }
      image {
        id
        fluid(quality: 100) {
          ...GatsbyContentfulFluid
        }
        file {
          url
          fileName
          contentType
        }
      }
      date(formatString: "D MMMM YYYY", locale: $locale)
      relatedNewsHeading {
        headingText
      }
      relatedNews {
        id
        title
        type
        slug
        date
        image {
          id
          fluid(quality: 75) {
            ...GatsbyContentfulFluid
          }
        }
        language
      }
      quote {
        id
        quoteText {
          id
          quoteText
        }
        author
        titleAndCompany
      }
    }
    
    localizations: allContentfulNewsPage(filter: {slug: { eq: $trueSlug }}){
     edges {
      node {
        slug
        language
      }
     }
    }

    news: allContentfulNewsPage(
      sort: { fields: [date], order: DESC }
      limit: 2
      filter: { slug: { ne: $trueSlug }, language: {eq: $locale} }
    ) {
      edges {
        node {
          id
          slug
          image {
            id
            fluid(quality: 75) {
              ...GatsbyContentfulFluid
            }
          }
          title
          date(formatString: "D MMMM YYYY", locale: "sv")
          type
          language
        }
      }
    }
  }
`;
